import { createAsyncThunk } from '@reduxjs/toolkit'

import { REQUEST_OPTIONS } from '../../../../constants/request-options'
import { AxiosService } from '../../../../services/api'
import { createThunkName } from '../../../../utils/createThunkname'
import {
  AddInsurerDto,
  DeleteInsurerDto,
  GetAllInsurerResponse,
  GetAllPlanQueryParams,
  GetAllPlanResponse,
  GetInsurerAgreementResponse,
  GetInsurerInfoResponse,
  GetInsurerMasterDataResponse,
  UpdateInsurerAgreementDto,
  UpdateInsurerInfoDto,
  UpdateInsurerStatusDto,
  UpdatePlanOrderDto
} from './types'
import { INSURER_URLS } from '../../../../configs/api'
import { saveData } from '../../../../utils/downloadFile'

const PREFIX = 'Insurer'

export const InsurerService = {
  getAllInsurerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getAllInsurer'), async () => {
    try {
      const result = await AxiosService.get<GetAllInsurerResponse>(INSURER_URLS.getAllInsurer)
      return result.data.insurers
    } catch (error) {
      throw error
    }
  }),
  exportInsurersAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'exportInsurers'), async () => {
    try {
      const result = await AxiosService.get(INSURER_URLS.exportInsurers, {
        ...REQUEST_OPTIONS
      })
      saveData(result, result.request.getResponseHeader('Content-Disposition'))
    } catch (error) {
      throw error
    }
  }),
  addInsurerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'addInsurer'), async (dto: AddInsurerDto) => {
    try {
      await AxiosService.post(INSURER_URLS.addInsurer, dto)
    } catch (error) {
      throw error
    }
  }),
  deleteInsurerAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'deleteInsurer'), async (dto: DeleteInsurerDto) => {
    try {
      await AxiosService.post(INSURER_URLS.deleteInsurerById, dto)
    } catch (error) {
      throw error
    }
  }),
  getInsurerInfoAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getInsurerInfo'), async (insurerId: string) => {
    try {
      const result = await AxiosService.get<GetInsurerInfoResponse>(INSURER_URLS.getInsurerInfo(insurerId))
      return result.data.insurer
    } catch (error) {
      throw error
    }
  }),
  getAllPlanAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getAllPlan'),
    async ({ insurerId, queryParams }: { insurerId: string; queryParams: GetAllPlanQueryParams }) => {
      try {
        const result = await AxiosService.get<GetAllPlanResponse>(INSURER_URLS.getAllPlan(insurerId), {
          params: queryParams
        })
        return result.data
      } catch (error) {
        throw error
      }
    }
  ),
  getInsurerAgreementAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'getInsurerAgreement'),
    async (insurerId: string) => {
      try {
        const result = await AxiosService.get<GetInsurerAgreementResponse>(INSURER_URLS.getInsurerAgreement(insurerId))
        return result.data.agreement
      } catch (error) {
        throw error
      }
    }
  ),
  updateInsurerInfoAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateInsurerInfo'),
    async (dto: UpdateInsurerInfoDto) => {
      try {
        await AxiosService.put(INSURER_URLS.updateInsurerInfo, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateInsurerAgreementAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateInsurerAgreement'),
    async (dto: UpdateInsurerAgreementDto) => {
      try {
        await AxiosService.put(INSURER_URLS.updateInsurerAgreement, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updatePlanOrderAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updatePlanOrder'),
    async (dto: UpdatePlanOrderDto) => {
      try {
        await AxiosService.put(INSURER_URLS.updatePlanOrder, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  updateInsurerStatusAsyncThunk: createAsyncThunk(
    createThunkName(PREFIX, 'updateInsurerStatus'),
    async (dto: UpdateInsurerStatusDto) => {
      try {
        await AxiosService.put(INSURER_URLS.updateInsurerStatus, dto)
      } catch (error) {
        throw error
      }
    }
  ),
  getInsurerMasterDataAsyncThunk: createAsyncThunk(createThunkName(PREFIX, 'getInsurerMasterData'), async () => {
    try {
      const result = await AxiosService.get<GetInsurerMasterDataResponse>(INSURER_URLS.masterData.getInsurerMasterData)
      return result.data.insurers
    } catch (error) {
      throw error
    }
  })
}
